<cipo-dialog-header
    [title]="dialogTitle()"
    [customCloseMode]="true"
    (closeClick)="tryClose()"
    closeTestId="add-new-attachment-close-button" />
<mat-dialog-content [class.upload-dialog-content]="dialogData.modalType === 'newFile'">
    @switch (dialogData.modalType) {
        @case ("newFile") {
            @if (dialogData.information) {
                <div class="upload-information cipo-text--black-400 m-b-sm">
                    <mat-icon class="cipo-icon cipo-icon--sm material-symbols-outlined" aria-hidden="true">
                        info
                    </mat-icon>
                    <span>{{ "fileExplorer.fileWillBeAdded" | translate }} {{ dialogData.information }}</span>
                </div>
            }

            @if (uploading() || (!multiple() && uploader.queue.length > 0)) {
                <div
                    class="upload-drop-zone upload-drop-zone--disabled"
                    data-testid="add-new-attachment-drop-zone-disabled">
                    <mat-icon class="material-symbols-outlined" aria-hidden="true">cloud_upload</mat-icon>
                    <h3>{{ "fileExplorer.dropFilesHere" | translate }}</h3>
                </div>
            } @else {
                <div
                    class="upload-drop-zone"
                    ng2FileDrop
                    [class.nv-file-over]="hasBaseDropZoneOver"
                    (fileOver)="fileOver($event)"
                    [uploader]="uploader"
                    role="group"
                    [attr.aria-label]="'a11y.fileExplorer.dropZone' | translate"
                    data-testid="add-new-attachment-drop-zone">
                    <mat-icon class="material-symbols-outlined" aria-hidden="true">cloud_upload</mat-icon>
                    <h3>{{ "fileExplorer.dropFilesHere" | translate }}</h3>
                    <button
                        mat-raised-button
                        color="primary"
                        type="button"
                        [disabled]="uploading()"
                        (click)="fileUploader.click()"
                        data-testid="add-new-attachment-select-button">
                        {{ "fileExplorer.selectFiles" | translate }}
                    </button>
                    <p>{{ "fileExplorer.uploadSelectHint" | translate }}</p>
                </div>
            }
            <input
                [disabled]="uploading() || (!multiple() && uploader.queue.length > 0)"
                type="file"
                #fileUploader
                ng2FileSelect
                [uploader]="uploader"
                [multiple]="multiple()"
                [attr.accept]="dialogData.accept || null"
                data-testid="add-new-attachment-file-input"
                class="cipo--display--none-important" />

            @if (uploader.queue.length) {
                <section
                    class="upload-selection"
                    [attr.aria-label]="'fileExplorer.selectedFiles' | translate: { count: uploader.queue.length }">
                    <div class="upload-selection-header">
                        <h3>{{ "fileExplorer.selectedFiles" | translate: { count: uploader.queue.length } }}</h3>
                        <button
                            mat-stroked-button
                            color="primary"
                            type="button"
                            [disabled]="uploading()"
                            (click)="removeAllFiles()"
                            data-testid="add-new-attachment-remove-all-button">
                            <mat-icon aria-hidden="true">delete_outline</mat-icon>
                            {{ "actions.removeAll" | translate }}
                        </button>
                    </div>

                    <div class="upload-table-scroll">
                        <table class="upload-table">
                            <thead>
                                <tr>
                                    <th scope="col">{{ "fileExplorer.name" | translate }}</th>
                                    <th scope="col">{{ "fileDialog.fileSize" | translate }}</th>
                                    <th scope="col">{{ "general.type" | translate }}</th>
                                    <th scope="col">{{ "fileExplorer.note" | translate }}</th>
                                    <th scope="col">{{ "general.status" | translate }}</th>
                                    <th scope="col">
                                        <span class="a11y-visually-hidden">{{ "actions.actions" | translate }}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                @for (item of uploader.queue; track item) {
                                    <tr>
                                        <td>
                                            <div class="upload-file-name">
                                                <img [src]="item.file['preview']" alt="" />
                                                <span [title]="item.file.name">{{ item.file.name }}</span>
                                            </div>
                                        </td>
                                        <td class="upload-file-meta">{{ formatFileSize(item.file.size) }}</td>
                                        <td class="upload-file-meta">{{ fileExtension(item.file.name) }}</td>
                                        <td class="upload-file-note">
                                            <label
                                                class="a11y-visually-hidden"
                                                [for]="'add-new-attachment-note-' + $index">
                                                {{ "a11y.fileExplorer.fileNote" | translate: { name: item.file.name } }}
                                            </label>
                                            <textarea
                                                matInput
                                                cdkTextareaAutosize
                                                cdkAutosizeMinRows="1"
                                                cdkAutosizeMaxRows="3"
                                                maxlength="1000"
                                                [id]="'add-new-attachment-note-' + $index"
                                                [disabled]="uploading()"
                                                [ngModel]="noteFor(item)"
                                                (ngModelChange)="setNote(item, $event)"
                                                [placeholder]="'fileExplorer.notePlaceholder' | translate"
                                                [attr.data-testid]="'add-new-attachment-note-' + $index"></textarea>
                                        </td>
                                        <td>
                                            <div class="upload-file-status" role="status">
                                                @if (item.isError) {
                                                    <mat-icon
                                                        class="material-symbols-outlined cipo-text--warn-500"
                                                        aria-hidden="true">
                                                        error
                                                    </mat-icon>
                                                    <span>{{ "fileExplorer.uploadError" | translate }}</span>
                                                } @else if (item.isUploaded) {
                                                    <mat-icon
                                                        class="material-icons cipo-text--accent-500"
                                                        aria-hidden="true">
                                                        check_circle
                                                    </mat-icon>
                                                    <span>{{ "fileExplorer.uploadComplete" | translate }}</span>
                                                } @else if (uploading()) {
                                                    <span>{{ "fileExplorer.uploading" | translate }}</span>
                                                } @else {
                                                    <mat-icon
                                                        class="material-icons cipo-text--accent-500"
                                                        aria-hidden="true">
                                                        check_circle
                                                    </mat-icon>
                                                    <span>{{ "general.ready" | translate }}</span>
                                                }
                                            </div>
                                            @if (uploading() && !item.isUploaded && !item.isError) {
                                                <mat-progress-bar
                                                    class="upload-file-progress"
                                                    [value]="item.progress"
                                                    [attr.aria-label]="
                                                        'a11y.fileExplorer.uploadProgress'
                                                            | translate: { name: item.file.name }
                                                    " />
                                            }
                                        </td>
                                        <td class="upload-file-action">
                                            <button
                                                mat-icon-button
                                                type="button"
                                                [disabled]="uploading()"
                                                (click)="removeFile(item)"
                                                [attr.aria-label]="
                                                    'a11y.fileExplorer.removeFile' | translate: { name: item.file.name }
                                                "
                                                [attr.data-testid]="'add-new-attachment-delete-file-' + $index">
                                                <mat-icon aria-hidden="true">close</mat-icon>
                                            </button>
                                        </td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                    <p class="upload-summary">
                        {{
                            "fileExplorer.uploadSummary"
                                | translate: { count: uploader.queue.length, size: formatFileSize(totalFileSize()) }
                        }}
                    </p>
                </section>
            }
        }
        @default {
            <div class="p-t-lg">
                <mat-form-field appearance="outline" class="cipo--width--full">
                    <mat-label>{{ "fileExplorer.name" | translate }}</mat-label>
                    <input matInput [formControl]="name" maxlength="255" data-testid="add-new-attachment-name-input" />
                    @if (dialogData.mimeType) {
                        <span matTextSuffix>{{ dialogData.mimeType }}</span>
                    }
                    @if (name.hasError("required")) {
                        <mat-error>{{ "errors.required" | translate }}</mat-error>
                    } @else if (name.hasError("maxlength")) {
                        <mat-error>{{ "errors.maxLength" | translate: { maxLength: 255 } }}</mat-error>
                    }
                </mat-form-field>
            </div>
        }
    }
</mat-dialog-content>
<mat-dialog-actions
    align="end"
    [class.upload-dialog-actions]="dialogData.modalType === 'newFile' && !warningContext()"
    [class.cipo-bg--warn-400]="warningContext()">
    @if (warningContext()) {
        <span class="m-r-sm" role="alert">{{ "general.discardMessage" | translate }}</span>
        <button
            mat-stroked-button
            type="button"
            (click)="dismissWarning()"
            data-testid="add-new-attachment-keep-editing-button">
            {{ "actions.cancel" | translate }}
        </button>
        <button
            mat-stroked-button
            type="button"
            color="warn"
            (click)="discardAndClose()"
            data-testid="add-new-attachment-discard-button">
            {{ "actions.discard" | translate }}
        </button>
        <button
            mat-flat-button
            type="button"
            color="primary"
            [disabled]="!valid() || uploading()"
            (click)="save()"
            data-testid="add-new-attachment-save-button">
            @if (dialogData.modalType === "newFile") {
                {{ "fileExplorer.uploadFilesCount" | translate: { count: uploader.queue.length } }}
            } @else {
                {{ "general.save" | translate }}
            }
        </button>
    } @else {
        @if (dialogData.modalType === "newFile" && dialogData.allowCreateVersion && uploader.queue.length) {
            <mat-checkbox
                class="upload-version-checkbox"
                [checked]="createNewVersion()"
                [disabled]="uploading()"
                (change)="toggleCreateVersion($event.checked)"
                data-testid="add-new-attachment-create-version">
                {{ "fileExplorer.createNewVersion" | translate }}
            </mat-checkbox>
        }
        <div class="upload-actions-buttons">
            <button mat-raised-button type="button" (click)="tryClose()" data-testid="add-new-attachment-cancel-button">
                {{ "general.cancel" | translate }}
            </button>
            <button
                mat-raised-button
                color="primary"
                [disabled]="!valid() || uploading()"
                (click)="save()"
                data-testid="add-new-attachment-save-button">
                @if (dialogData.modalType === "newFile") {
                    {{ "fileExplorer.uploadFilesCount" | translate: { count: uploader.queue.length } }}
                } @else {
                    {{ "general.save" | translate }}
                }
            </button>
        </div>
    }
</mat-dialog-actions>
